/* ──────────────────────────────────────────────────────────────
   Distribution/ApiKeys/index.tsx · API keys (live — /api/v1/api-keys)
   ────────────────────────────────────────────────────────────── */

import React, { useState, useEffect, useCallback } from 'react';
import { useReloadOnFocus } from '../../navigation/useReloadOnFocus';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  ViewStyle,
  TextStyle,
} from 'react-native';

import { useTheme } from '../../context/themeContext';
import { useResponsive, CONTENT } from '../../lib/responsive';
import {
  Icon,
  Button,
  Chip,
  Input,
  Alert,
  Modal,
  Empty,
  Popover,
  MenuItem,
  MenuSeparator,
  ConfirmDialog,
  useToast,
} from '../../components/ui';
import {
  getApiKeys,
  createApiKey,
  rotateApiKey,
  deleteApiKey,
} from '../../services/catalog/apiKey';
import { handleApiError } from '../../utils/errorHandler';
import { showSuccess } from '../../utils/successHandler';
import type { ApiKey, ApiKeyScope } from '../../schemas/catalog/apiKey';

// ── KeyMono ───────────────────────────────────────────────────

function KeyMono({ children }: { children: React.ReactNode }) {
  const { theme } = useTheme();
  const { colors, fontSize, radii } = theme;
  return (
    <Text style={{ fontFamily: theme.font.mono, fontSize: fontSize.sm, color: colors.ink2, backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line, borderRadius: radii.r2, paddingVertical: 4, paddingHorizontal: 10 }}>
      {children}
    </Text>
  );
}

// ── ApiKeyRow ─────────────────────────────────────────────────

interface ApiKeyRowProps {
  apiKey: ApiKey;
  onRotate: (k: ApiKey) => void;
  onRevoke: (k: ApiKey) => void;
}

function ApiKeyRow({ apiKey: k, onRotate, onRevoke }: ApiKeyRowProps) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight } = theme;
  const toast = useToast();
  const { isMobile } = useResponsive();

  const isWrite = (k.scopes || []).some(s => s.includes('write') || s.includes('manage'));

  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 10, paddingHorizontal: 12, borderBottomWidth: 1, borderBottomColor: colors.line, gap: 12, flexWrap: 'nowrap' }}>
      <View style={{ flex: isMobile ? 1 : 2, minWidth: isMobile ? 0 : 120 }}>
        <Text numberOfLines={1} style={{ fontWeight: fontWeight.medium, fontSize: fontSize.md, color: colors.ink, fontFamily: theme.font.sans }}>{k.name}</Text>
        <Text numberOfLines={1} style={{ fontFamily: theme.font.mono, fontSize: fontSize.xs, color: colors.ink3 }}>
          {k.createdAt ? `Created ${new Date(k.createdAt).toLocaleDateString()}` : ''}
        </Text>
        {/* Mobile meta line — folds the hidden Scope/Key/Status/Last-used columns in. */}
        {isMobile && (
          <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
            <Chip tone={k.status === 'ACTIVE' ? 'success' : 'neutral'} dot>{k.status}</Chip>
            <Chip tone={isWrite ? 'warn' : 'neutral'}>{isWrite ? 'read + write' : 'read'}</Chip>
            <Text numberOfLines={1} style={{ fontFamily: theme.font.mono, fontSize: fontSize.xs, color: colors.ink4 }}>{k.keyPrefix}••••••</Text>
          </View>
        )}
      </View>

      {!isMobile && (
        <View style={{ width: 90 }}>
          <Chip tone={isWrite ? 'warn' : 'neutral'}>{isWrite ? 'read + write' : 'read'}</Chip>
        </View>
      )}

      {!isMobile && (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, width: 190 }}>
          <KeyMono>{k.keyPrefix}••••••</KeyMono>
        </View>
      )}

      {!isMobile && (
        <View style={{ width: 80 }}>
          <Chip tone={k.status === 'ACTIVE' ? 'success' : 'neutral'} dot>{k.status}</Chip>
        </View>
      )}

      {!isMobile && (
        <View style={{ width: 90 }}>
          <Text style={{ fontSize: fontSize.sm, color: colors.ink3, fontFamily: theme.font.sans }}>
            {k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleDateString() : 'Never used'}
          </Text>
        </View>
      )}

      {/* Delete column — icon centered under the DELETE header. */}
      <View style={{ width: 64, alignItems: 'center' }}>
        <Button kind="danger" size="xs" iconOnly icon="trash" style={{ alignSelf: 'center' }} aria-label={`Delete key ${k.name}`} onPress={() => onRevoke(k)} />
      </View>

      <View style={{ width: 34, alignItems: 'center' }}>
        <Popover align="right" width={160} trigger={<Button kind="ghost" size="sm" icon="more" iconOnly />}>
          {(close: () => void) => (
            <>
              <MenuItem icon="copy" onPress={() => { close(); toast.success('Prefix copied'); }}>Copy prefix</MenuItem>
              <MenuItem icon="refresh" onPress={() => { close(); onRotate(k); }}>Rotate</MenuItem>
              <MenuSeparator />
              <MenuItem icon="trash" danger onPress={() => { close(); onRevoke(k); }}>Revoke</MenuItem>
            </>
          )}
        </Popover>
      </View>
    </View>
  );
}

// ── NewApiKeyModal ────────────────────────────────────────────

interface NewApiKeyModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (secret: string, name: string) => void;
}

function NewApiKeyModal({ open, onClose, onCreated }: NewApiKeyModalProps) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii } = theme;
  const [name, setName] = useState('');
  const [scope, setScope] = useState<'read' | 'readwrite'>('read');
  const [busy, setBusy] = useState(false);

  const pill = (active: boolean): ViewStyle => ({
    flexDirection: 'row', alignItems: 'center', gap: 6, height: 30, paddingHorizontal: 10,
    borderWidth: 1, borderColor: active ? colors.accentLine : colors.line, borderRadius: radii.r2,
    backgroundColor: active ? colors.accentSoft : colors.card,
    ...(Platform.select({ web: { cursor: 'pointer', userSelect: 'none' } as any }) ?? {}),
  });
  const pillText = (active: boolean): TextStyle => ({ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: active ? colors.accentInk : colors.ink2, fontFamily: theme.font.sans });

  const onSubmit = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const scopes: ApiKeyScope[] = scope === 'read'
        ? ['catalog:read']
        : ['catalog:read', 'catalog:write', 'taxonomy:write', 'media:write'];
      const res = await createApiKey({ name: name.trim(), scopes });
      onCreated(res.secret, res.name);
      setName(''); setScope('read');
    } catch (err) {
      handleApiError(err, 'Could not create key');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create API key"
      footer={
        <>
          <Button onPress={onClose}>Cancel</Button>
          <Button kind="accent" icon="key" disabled={!name.trim() || busy} onPress={onSubmit}>
            {busy ? 'Creating…' : 'Create key'}
          </Button>
        </>
      }
    >
      <View style={{ gap: 16 }}>
        <Input label="Key name" placeholder="e.g. Mobile app" value={name} onChangeText={setName} autoFocus />
        <View>
          <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink2, marginBottom: 8, fontFamily: theme.font.sans }}>Scope</Text>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <TouchableOpacity style={pill(scope === 'read')} onPress={() => setScope('read')}>
              <Icon name="eye" size={13} color={scope === 'read' ? colors.accentInk : colors.ink2} />
              <Text style={pillText(scope === 'read')}>Read</Text>
            </TouchableOpacity>
            <TouchableOpacity style={pill(scope === 'readwrite')} onPress={() => setScope('readwrite')}>
              <Icon name="edit" size={13} color={scope === 'readwrite' ? colors.accentInk : colors.ink2} />
              <Text style={pillText(scope === 'readwrite')}>Read + write</Text>
            </TouchableOpacity>
          </View>
        </View>
        <Alert tone="warn" icon="warn">Write keys can change your catalog. Grant sparingly.</Alert>
      </View>
    </Modal>
  );
}

// ── Main screen ───────────────────────────────────────────────

export default function ApiKeysScreen() {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii, shadows } = theme;
  const { isMobile, pad } = useResponsive();
  const toast = useToast();

  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);
  const [freshSecret, setFreshSecret] = useState<{ name: string; secret: string } | null>(null);
  const [delTarget, setDelTarget] = useState<ApiKey | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getApiKeys();
      setKeys(res.items ?? []);
    } catch (err) {
      handleApiError(err, 'Could not load API keys');
    } finally {
      setLoading(false);
    }
  }, []);

  useReloadOnFocus(load);

  const onRotate = async (k: ApiKey) => {
    try {
      const res = await rotateApiKey(k.id);
      setFreshSecret({ name: res.name, secret: res.secret });
      showSuccess(res, 'Key rotated', 'Copy the new secret now — it won’t be shown again.');
      await load();
    } catch (err) {
      handleApiError(err, 'Could not rotate key');
    }
  };

  const onRevoke = (k: ApiKey) => setDelTarget(k);

  const confirmDelete = async () => {
    if (!delTarget) return;
    setDeleting(true);
    try {
      await deleteApiKey(delTarget.id);
      showSuccess({}, 'Key revoked', `“${delTarget.name}” revoked.`);
      // Revoke is a soft-delete: keep the key on screen, just mark it REVOKED.
      setKeys(prev => prev.map(x => (x.id === delTarget.id ? { ...x, status: 'REVOKED' } : x)));
      setDelTarget(null);
    } catch (err) {
      handleApiError(err, 'Could not revoke key');
    } finally {
      setDeleting(false);
    }
  };

  const th: TextStyle = {
    fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: colors.ink3, textTransform: 'uppercase',
    letterSpacing: 0.04 * fontSize.xs, fontFamily: theme.font.sans, paddingVertical: 10,
  };

  return (
    <ScrollView style={{ flex: 1, backgroundColor: colors.paper }} contentContainerStyle={{ padding: pad, paddingBottom: 64, maxWidth: CONTENT.default, width: '100%' }} showsVerticalScrollIndicator={false}>
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 20 }}>
        <View style={{ flex: 1 }}>
          <Text style={{ fontSize: fontSize['3xl'], fontWeight: fontWeight.semibold, color: colors.ink, letterSpacing: -0.02 * fontSize['3xl'], fontFamily: theme.font.sans }}>API keys</Text>
          <Text style={{ fontSize: fontSize.md, color: colors.ink3, marginTop: 4, fontFamily: theme.font.sans }}>
            Scoped, per-consumer credentials — read-only by default.
          </Text>
        </View>
        <Button kind="accent" icon="plus" onPress={() => setNewOpen(true)}>Create key</Button>
      </View>

      {freshSecret && (
        <View style={{ marginBottom: 16 }}>
          <Alert tone="success" icon="key" title={`Secret for “${freshSecret.name}” — copy it now`}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
              <KeyMono>{freshSecret.secret}</KeyMono>
              <Button kind="ghost" size="xs" icon="copy" onPress={() => toast.success('Secret copied')}>Copy</Button>
              <Button kind="ghost" size="xs" icon="x" onPress={() => setFreshSecret(null)}>Dismiss</Button>
            </View>
          </Alert>
        </View>
      )}

      <View style={{ backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line, borderRadius: radii.r3, overflow: 'hidden', ...(shadows.sm as ViewStyle) }}>
        {!isMobile && (
          <View style={{ flexDirection: 'row', paddingHorizontal: 12, gap: 12, borderBottomWidth: 1, borderBottomColor: colors.line, backgroundColor: colors.paper2 }}>
            <View style={{ flex: 2, minWidth: 120 }}><Text style={th}>Name</Text></View>
            <View style={{ width: 90 }}><Text style={th}>Scope</Text></View>
            <View style={{ width: 190 }}><Text style={th}>Key</Text></View>
            <View style={{ width: 80 }}><Text style={th}>Status</Text></View>
            <View style={{ width: 90 }}><Text style={th}>Last used</Text></View>
            <View style={{ width: 64, alignItems: 'center' }}><Text style={th}>Delete</Text></View>
            <View style={{ width: 34 }} />
          </View>
        )}

        {loading ? (
          <View style={{ paddingVertical: 36, alignItems: 'center' }}><ActivityIndicator color={colors.accent} /></View>
        ) : keys.length === 0 ? (
          <View style={{ padding: 16 }}><Empty title="No API keys yet">Create a scoped key to access the catalog API.</Empty></View>
        ) : (
          keys.map(k => <ApiKeyRow key={k.id} apiKey={k} onRotate={onRotate} onRevoke={onRevoke} />)
        )}
      </View>

      <NewApiKeyModal
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onCreated={(secret, name) => { setNewOpen(false); setFreshSecret({ secret, name }); load(); }}
      />

      <ConfirmDialog
        visible={!!delTarget}
        title="Revoke API key?"
        message={delTarget ? `"${delTarget.name}" will stop working immediately. This cannot be undone.` : ''}
        confirmText={deleting ? 'Revoking…' : 'Revoke'}
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => setDelTarget(null)}
        onClose={() => setDelTarget(null)}
      />
    </ScrollView>
  );
}
